import { useCallback, useEffect, useRef, useState } from 'react';

import { toast } from '@utilities';
import { downloadPresentation } from '@utilities/powerpoint';
import { setEndpoint } from '@helpers';

import {
  buildNarrativeDownloadableData,
  buildNarrativeDownloadFileName,
} from '../../../components/NarrativeFlowView/utilities/helpers';
import { buildNarrativePresentation } from './buildNarrativePresentation';

const PPT_EXPORT_TIMEOUT_MS = 10000;

export const downloadNarrativeXlsx = (flowId) => {
  const downloadLink = document.createElement('a');
  downloadLink.href = setEndpoint(`api/flows/${flowId}/export`);
  downloadLink.click();
};

export const useNarrativePptExport = () => {
  const [pendingPptExport, setPendingPptExport] = useState(null);
  const pptExportTimeoutIdRef = useRef(null);

  useEffect(() => {
    if (!pendingPptExport?.flowId) return undefined;

    pptExportTimeoutIdRef.current = window.setTimeout(() => {
      pptExportTimeoutIdRef.current = null;
      setPendingPptExport(null);
      toast.error('Unable to prepare PowerPoint export. Please try again.');
    }, PPT_EXPORT_TIMEOUT_MS);

    return () => {
      window.clearTimeout(pptExportTimeoutIdRef.current);
      pptExportTimeoutIdRef.current = null;
    };
  }, [pendingPptExport]);

  const handlePptExportError = useCallback(() => {
    if (pptExportTimeoutIdRef.current) {
      window.clearTimeout(pptExportTimeoutIdRef.current);
      pptExportTimeoutIdRef.current = null;
    }
    setPendingPptExport(null);
    toast.error('Unable to prepare PowerPoint export. Please try again.');
  }, []);

  const handlePptExportReady = useCallback(async ({ downloadData, flowData, request }) => {
    if (!request?.flowId || String(request.flowId) !== String(flowData?.id)) return;

    if (pptExportTimeoutIdRef.current) {
      window.clearTimeout(pptExportTimeoutIdRef.current);
      pptExportTimeoutIdRef.current = null;
    }

    try {
      const exportData = buildNarrativeDownloadableData({ downloadData, flowData });
      if (!exportData) throw new Error('Missing export data');

      const model = await buildNarrativePresentation({
        exportData,
        fileName: buildNarrativeDownloadFileName(flowData),
      });
      await downloadPresentation(model);
    } catch (error) {
      console.error('Failed to export narrative PowerPoint', error);
      toast.error('Unable to download PowerPoint. Please try again.');
    } finally {
      setPendingPptExport(null);
    }
  }, []);

  const startPptExport = useCallback((flowId) => {
    if (flowId == null) return;
    setPendingPptExport({ flowId });
  }, []);

  return {
    handlePptExportError,
    handlePptExportReady,
    pendingPptExport,
    startPptExport,
  };
};
